var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Add as AddIcon, DeleteOutline as DeleteIcon, Stop as StopIcon } from "@mui/icons-material";
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Snackbar, Tab, Tabs, TextField, Typography, useTheme, } from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useExportProgress } from "../../hooks/useExportProgress";
import apiService from "../../services/api";
import { useUsage } from "../../hooks/useUsage";
import { UsageLimitError } from "../../services/usageLimits";
import { describeEnrichmentResult } from "./enrichmentResultMessage";
import listService from "../../services/listService";
import CandidatesTable from "../Candidates/CandidatesTable";
import EnrichmentSelectionDialog from "./EnrichmentSelectionDialog";
const ListsContainer = () => {
    var _a;
    const { token, user } = useAuth();
    const { refreshUsage } = useUsage({ enabled: false });
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const exportListId = searchParams.get("listId");
    const isExportingParam = searchParams.get("exporting") === "true";
    const exportSessionId = searchParams.get("sessionId");
    const exportProgress = useExportProgress(isExportingParam ? exportSessionId : null);
    const [activeTab, setActiveTab] = useState(exportListId || "all-candidates");
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newListName, setNewListName] = useState("");
    const [listToDelete, setListToDelete] = useState(null);
    const [isDeletingList, setIsDeletingList] = useState(false);
    const [candidateIdsToDelete, setCandidateIdsToDelete] = useState([]);
    const [isDeletingCandidates, setIsDeletingCandidates] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    // Enrichment state
    const [showEnrichmentDialog, setShowEnrichmentDialog] = useState(false);
    const [isEnriching, setIsEnriching] = useState(false);
    const [enrichmentProgress, setEnrichmentProgress] = useState(undefined);
    const [enrichingCandidateIds, setEnrichingCandidateIds] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    useEffect(() => {
        if (exportListId) {
            setActiveTab(exportListId);
        }
    }, [exportListId]);
    // Fetch user's lists
    const { data: listsData, isLoading: listsLoading, refetch: refetchLists, } = useQuery("user-lists", () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(token && user))
            throw new Error("No token or user available");
        return listService.getUserLists(user._id, token);
    }), {
        enabled: !!token && !!user,
    });
    // Fetch all candidates count
    const { data: allCandidatesData, refetch: refetchAllCandidates } = useQuery("all-candidates", () => __awaiter(void 0, void 0, void 0, function* () {
        if (!token)
            throw new Error("No token available");
        return apiService.getAllCandidatesByUser(token);
    }), {
        enabled: !!token,
    });
    // Fetch candidates for the active list
    const { isLoading: candidatesLoading, refetch: refetchCandidates } = useQuery(["list-candidates", activeTab], () => __awaiter(void 0, void 0, void 0, function* () {
        if (!token)
            throw new Error("No token available");
        if (activeTab === "all-candidates") {
            return apiService.getAllCandidatesByUser(token);
        }
        if (activeTab === "no-lists") {
            return apiService.getCandidatesWithoutLists(token);
        }
        // Regular list
        return apiService.getCandidatesByList(token, activeTab);
    }), {
        enabled: !!token && !!activeTab,
        keepPreviousData: true,
        refetchInterval: exportProgress.isExporting ? 5000 : false,
        onSuccess: (data) => {
            setCandidates((data === null || data === void 0 ? void 0 : data.candidates) || []);
        },
    });
    useEffect(() => {
        if (exportProgress.isExporting || exportProgress.isComplete) {
            refetchCandidates();
            refetchLists();
        }
        if (exportProgress.isComplete && isExportingParam) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete("exporting");
                next.delete("sessionId");
                return next;
            }, { replace: true });
        }
    }, [exportProgress.isExporting, exportProgress.isComplete, isExportingParam, refetchCandidates, refetchLists, setSearchParams]);
    // Build tabs list
    const tabs = [
        {
            _id: "all-candidates",
            name: "All Contacts",
            candidateCount: ((_a = allCandidatesData === null || allCandidatesData === void 0 ? void 0 : allCandidatesData.candidates) === null || _a === void 0 ? void 0 : _a.length) || 0,
            isSpecial: true,
            specialType: "all",
        },
        ...((listsData === null || listsData === void 0 ? void 0 : listsData.lists) || []),
    ];
    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
        setSelectedRowIds([]);
    };
    const handleCreateList = () => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        try {
            if (!(token && user && newListName.trim()))
                return;
            const result = yield listService.createList(newListName.trim(), token);
            if (result.success) {
                setCreateModalOpen(false);
                setNewListName("");
                yield refetchLists();
                if ((_b = result.list) === null || _b === void 0 ? void 0 : _b._id) {
                    setActiveTab(result.list._id);
                }
            }
        }
        catch (error) {
            console.error("Error creating list:", error);
        }
    });
    const handleDeleteList = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(token && listToDelete))
            return;
        setIsDeletingList(true);
        const result = yield listService.deleteList(listToDelete._id, token);
        setIsDeletingList(false);
        if (!result.success) {
            setSnackbar({ open: true, message: `Could not delete list: ${result.error}`, severity: "error" });
            return;
        }
        setSnackbar({ open: true, message: `Deleted list "${listToDelete.name}"`, severity: "success" });
        setListToDelete(null);
        setActiveTab("all-candidates");
        setSelectedRowIds([]);
        yield refetchLists();
    });
    const handleDeleteCandidates = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!token || candidateIdsToDelete.length === 0)
            return;
        setIsDeletingCandidates(true);
        const result = yield listService.deleteCandidates(candidateIdsToDelete, token);
        setIsDeletingCandidates(false);
        if (!result.success) {
            setSnackbar({ open: true, message: `Could not delete contacts: ${result.error}`, severity: "error" });
            return;
        }
        const deletedIds = candidateIdsToDelete;
        setSnackbar({ open: true, message: `Deleted ${result.deletedCount} contact(s)`, severity: "success" });
        setCandidateIdsToDelete([]);
        setCandidates((prev) => prev.filter((c) => !deletedIds.includes(c._id)));
        setSelectedRowIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
        yield Promise.all([refetchCandidates(), refetchAllCandidates(), refetchLists()]);
    });
    const handleRemoveCandidateFromList = (linkedInId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!token || activeTab === "all-candidates" || activeTab === "no-lists")
                return;
            const result = yield listService.removeCandidateFromList(linkedInId, activeTab, token);
            if (result.success) {
                setCandidates((prev) => prev.filter((c) => c.linkedInId !== linkedInId));
                refetchCandidates();
                refetchLists();
            }
        }
        catch (error) {
            console.error("Error removing candidate from list:", error);
        }
    });
    const handleOpenEnrichmentDialog = () => {
        setShowEnrichmentDialog(true);
    };
    const handleConfirmEnrichment = (selectedTypes, enrichmentCount, aiPrompt) => __awaiter(void 0, void 0, void 0, function* () {
        if (!token || selectedTypes.length === 0 || candidates.length === 0) {
            console.error("Missing required data:", { token: !!token, selectedTypes, candidatesLength: candidates.length });
            return;
        }
        setShowEnrichmentDialog(false);
        setIsEnriching(true);
        try {
            // Get the candidates to enrich
            let candidatesToEnrich;
            if (selectedRowIds.length > 0) {
                candidatesToEnrich = candidates.filter((c) => selectedRowIds.includes(c._id)).slice(0, enrichmentCount);
            }
            else {
                candidatesToEnrich = candidates.slice(0, enrichmentCount);
            }
            const candidateIds = candidatesToEnrich.map((c) => c._id).filter((id) => id && typeof id === "string");
            if (candidateIds.length === 0) {
                throw new Error("No valid candidate IDs found");
            }
            // Process each enrichment type sequentially
            for (const type of selectedTypes) {
                const enrichmentType = type;
                // Skip integrations and emailVerification from regular enrichment flow (handled separately)
                if (enrichmentType === "integrations" || enrichmentType === "emailVerification") {
                    continue;
                }
                setEnrichmentProgress({
                    completed: 0,
                    total: candidatesToEnrich.length,
                    type: enrichmentType,
                });
                try {
                    // Use streaming API for AI enrichment
                    if (enrichmentType === "aiPrompts") {
                        // Set all candidates as enriching
                        setEnrichingCandidateIds(candidateIds);
                        yield new Promise((resolve, reject) => {
                            let enrichedCount = 0;
                            let failedCount = 0;
                            const cleanup = apiService.enrichCandidatesStream(token, candidateIds, "aiPrompts", aiPrompt, (data) => {
                                if (data.event === "progress" && data.enrichedCandidate) {
                                    // Update the candidate in the local state
                                    setCandidates((prev) => prev.map((c) => { var _a; return (c._id === data.candidateId ? ((_a = data.enrichedCandidate) !== null && _a !== void 0 ? _a : c) : c); }));
                                    // Remove this candidate from enriching list
                                    setEnrichingCandidateIds((prev) => prev.filter((id) => id !== data.candidateId));
                                    enrichedCount++;
                                }
                                else if (data.event === "error") {
                                    // Remove failed candidate from enriching list
                                    if (data.candidateId) {
                                        setEnrichingCandidateIds((prev) => prev.filter((id) => id !== data.candidateId));
                                    }
                                    failedCount++;
                                }
                                setEnrichmentProgress({ completed: data.completed, total: data.total, type: enrichmentType });
                            }, (error) => {
                                console.error("Stream error:", error);
                                setEnrichingCandidateIds([]);
                                reject(error);
                            }, () => {
                                setEnrichingCandidateIds([]);
                                setSnackbar({
                                    open: true,
                                    message: `Successfully enriched ${enrichedCount} candidates with AI${failedCount > 0 ? ` (${failedCount} failed)` : ""}`,
                                    severity: enrichedCount > 0 ? "success" : "info",
                                });
                                resolve();
                            });
                            return cleanup;
                        });
                    }
                    else {
                        // Use regular API for non-AI enrichments
                        const result = yield apiService.enrichCandidates(token, candidateIds, enrichmentType, aiPrompt);
                        if (result.status === "completed") {
                            setSnackbar({
                                open: true,
                                message: describeEnrichmentResult(result, enrichmentType),
                                severity: "success",
                            });
                        }
                        // Refresh candidates after non-AI enrichments
                        refetchCandidates();
                    }
                }
                catch (error) {
                    // A plan limit already opened the upgrade / top-up dialog; the other selected types would hit it too
                    if (error instanceof UsageLimitError)
                        break;
                    console.error(`Error enriching with ${enrichmentType}:`, error);
                    setSnackbar({
                        open: true,
                        message: `Failed to enrich with ${enrichmentType}. Please try again.`,
                        severity: "error",
                    });
                }
            }
        }
        catch (error) {
            console.error("Error during enrichment:", error);
            setSnackbar({
                open: true,
                message: `Enrichment failed: ${error instanceof Error ? error.message : "Unknown error"}`,
                severity: "error",
            });
        }
        finally {
            setIsEnriching(false);
            setEnrichmentProgress(undefined);
            refreshUsage();
        }
    });
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const isSpecialList = activeTab === "all-candidates" || activeTab === "no-lists";
    if (listsLoading) {
        return (_jsx(Box, Object.assign({ sx: { display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" } }, { children: _jsx(CircularProgress, {}) })));
    }
    return (_jsxs(Box, Object.assign({ sx: { height: "100%", display: "flex", flexDirection: "column" } }, { children: [_jsx(Box, Object.assign({ sx: {
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: theme.palette.background.paper,
                    position: "relative",
                    zIndex: 10,
                } }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1 } }, { children: [_jsx(Tabs, Object.assign({ value: activeTab, onChange: handleTabChange, variant: "scrollable", scrollButtons: "auto", sx: {
                                flex: 1,
                                "& .MuiTab-root": {
                                    textTransform: "none",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    minHeight: 48,
                                    color: theme.palette.text.secondary,
                                    "&.Mui-selected": {
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                    },
                                },
                                "& .MuiTabs-indicator": {
                                    height: 3,
                                    borderRadius: "3px 3px 0 0",
                                },
                            } }, { children: tabs.map((tab) => (_jsx(Tab, { value: tab._id, label: _jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 1 } }, { children: [tab.name, tab.candidateCount !== undefined && (_jsx(Box, Object.assign({ component: "span", sx: {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                color: theme.palette.primary.main,
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: "12px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                minWidth: "20px",
                                                textAlign: "center",
                                            } }, { children: tab.candidateCount }))), !tab.isSpecial && activeTab === tab._id && (_jsx(IconButton, Object.assign({ size: "small", onClick: (e) => {
                                                e.stopPropagation();
                                                setListToDelete(tab);
                                            }, "aria-label": `Delete list ${tab.name}`, sx: {
                                                ml: 0.5,
                                                p: 0.25,
                                                "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.1) },
                                            } }, { children: _jsx(DeleteIcon, { sx: { fontSize: 16 } }) })))] })) }, tab._id))) })), _jsx(Button, Object.assign({ variant: "outlined", startIcon: _jsx(AddIcon, {}), onClick: () => setCreateModalOpen(true), sx: {
                                ml: 2,
                                textTransform: "none",
                                borderRadius: "6px",
                                flexShrink: 0,
                            } }, { children: "Add List" }))] })) })), exportProgress.isExporting && (_jsxs(Box, Object.assign({ sx: {
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                } }, { children: [_jsxs(Box, Object.assign({ sx: { flex: 1 } }, { children: [_jsxs(Typography, Object.assign({ variant: "body2", sx: { mb: 0.5 } }, { children: ["Exporting...", " ", _jsxs("strong", { children: [exportProgress.exportedCount, " / ", exportProgress.exportTotal] }), " ", "contacts"] })), _jsx(LinearProgress, { variant: "determinate", value: exportProgress.exportTotal > 0 ? (exportProgress.exportedCount / exportProgress.exportTotal) * 100 : 0 })] })), _jsx(Button, Object.assign({ variant: "outlined", size: "small", color: "error", startIcon: _jsx(StopIcon, {}), onClick: exportProgress.cancelExport }, { children: "Cancel" }))] }))), exportProgress.error && (_jsxs(Alert, Object.assign({ severity: "error", sx: { mx: 2, mt: 1 } }, { children: [exportProgress.error, exportProgress.exportedCount > 0 && ` (${exportProgress.exportedCount} contacts were saved)`] }))), _jsx(Box, Object.assign({ sx: { flex: 1, minHeight: 0, p: 1 } }, { children: candidatesLoading && candidates.length === 0 ? (_jsx(Box, Object.assign({ sx: { display: "flex", justifyContent: "center", alignItems: "center", height: "100%" } }, { children: _jsx(CircularProgress, {}) }))) : candidates.length === 0 ? (_jsx(Alert, Object.assign({ severity: "info", sx: { m: 2 } }, { children: "No candidates in this list." }))) : (_jsx(CandidatesTable, { candidates: candidates, loading: candidatesLoading && candidates.length === 0, onRemoveFromList: !isSpecialList ? handleRemoveCandidateFromList : undefined, showListActions: !isSpecialList, showEnrichedColumns: true, selectedRowIds: selectedRowIds, onSelectionChange: setSelectedRowIds, onEnrich: handleOpenEnrichmentDialog, enrichingCandidateIds: enrichingCandidateIds, onDeleteCandidates: activeTab === "all-candidates" ? setCandidateIdsToDelete : undefined })) })), _jsxs(Dialog, Object.assign({ open: createModalOpen, onClose: () => setCreateModalOpen(false), maxWidth: "sm", fullWidth: true }, { children: [_jsx(DialogTitle, { children: "Create New List" }), _jsx(DialogContent, { children: _jsx(TextField, { autoFocus: true, margin: "dense", label: "List Name", fullWidth: true, variant: "outlined", value: newListName, onChange: (e) => setNewListName(e.target.value), onKeyDown: (e) => {
                                if (e.key === "Enter" && newListName.trim()) {
                                    handleCreateList();
                                }
                            }, autoComplete: "off", sx: { mt: 1 } }) }), _jsxs(DialogActions, { children: [_jsx(Button, Object.assign({ onClick: () => setCreateModalOpen(false) }, { children: "Cancel" })), _jsx(Button, Object.assign({ onClick: handleCreateList, variant: "contained", disabled: !newListName.trim() }, { children: "Create" }))] })] })), _jsxs(Dialog, Object.assign({ open: !!listToDelete, onClose: () => !isDeletingList && setListToDelete(null), maxWidth: "xs", fullWidth: true }, { children: [_jsx(DialogTitle, { children: "Delete list?" }), _jsx(DialogContent, { children: _jsxs(Typography, Object.assign({ variant: "body2" }, { children: ["Delete ", _jsx("strong", { children: listToDelete === null || listToDelete === void 0 ? void 0 : listToDelete.name }), "? The contacts in it are kept and still show under All Contacts."] })) }), _jsxs(DialogActions, { children: [_jsx(Button, Object.assign({ onClick: () => setListToDelete(null), disabled: isDeletingList }, { children: "Cancel" })), _jsx(Button, Object.assign({ onClick: handleDeleteList, color: "error", variant: "contained", disabled: isDeletingList }, { children: isDeletingList ? "Deleting..." : "Delete" }))] })] })), _jsxs(Dialog, Object.assign({ open: candidateIdsToDelete.length > 0, onClose: () => !isDeletingCandidates && setCandidateIdsToDelete([]), maxWidth: "xs", fullWidth: true }, { children: [_jsxs(DialogTitle, { children: ["Delete ", candidateIdsToDelete.length, " contact", candidateIdsToDelete.length === 1 ? "" : "s", "?"] }), _jsx(DialogContent, { children: _jsx(Typography, Object.assign({ variant: "body2" }, { children: "They are removed from your contacts and from all your lists. Teammates who saved the same profiles keep them." })) }), _jsxs(DialogActions, { children: [_jsx(Button, Object.assign({ onClick: () => setCandidateIdsToDelete([]), disabled: isDeletingCandidates }, { children: "Cancel" })), _jsx(Button, Object.assign({ onClick: handleDeleteCandidates, color: "error", variant: "contained", disabled: isDeletingCandidates }, { children: isDeletingCandidates ? "Deleting..." : "Delete" }))] })] })), _jsx(EnrichmentSelectionDialog, { open: showEnrichmentDialog, onClose: () => setShowEnrichmentDialog(false), onConfirm: handleConfirmEnrichment, 
                // Enrichment uses the ticked rows when there are any, so the dialog counts and prices those
                candidateCount: selectedRowIds.length > 0 ? selectedRowIds.length : candidates.length, isSelection: selectedRowIds.length > 0, loading: isEnriching }), isEnriching && enrichmentProgress && (_jsxs(Box, Object.assign({ sx: {
                    position: "fixed",
                    bottom: 80,
                    right: 24,
                    width: 400,
                    zIndex: 1300,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    boxShadow: 3,
                    p: 2,
                } }, { children: [_jsxs(Typography, Object.assign({ variant: "body2", gutterBottom: true }, { children: ["Enriching ", enrichmentProgress.type, " data...", " ", _jsxs("strong", { children: [enrichmentProgress.completed, " / ", enrichmentProgress.total] }), " ", "candidates"] })), _jsx(LinearProgress, { variant: "determinate", value: (enrichmentProgress.completed / enrichmentProgress.total) * 100, sx: { mt: 1 } })] }))), _jsx(Snackbar, Object.assign({ open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: "bottom", horizontal: "right" } }, { children: _jsx(Alert, Object.assign({ onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: "100%" } }, { children: snackbar.message })) }))] })));
};
export default ListsContainer;
